import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Jellyfish — a soft translucent-pink dome bell with a cream frill at the rim
// and a curtain of wavy tentacles dangling to the floor. In the default pose
// the tentacle tips touch the ground so the little jelly reads as standing on
// its own skirt, not floating. Big close-set eyes on the front of the bell.
// Mess batch: chunky toy proportions, warm saturated colours.
//
// Frame: faces +Z (the eyes), tentacle tips on the ground at y = 0. ~0.6 m
// tall to the top of the bell, ~0.45 m across.
//
// Rig (flat, each named node about its own pivot):
//   - "bell"  : the dome + frill + face. Pulses (scale about the rim) and
//               bobs — the signature move.
//   - "tent-0" .. "tent-3" : four of the eight tentacles (the corner ones)
//               are named so they can sway in alternating phases; the rest
//               hang straight for a grounded read.

const PINK = "#f2a0bd";        // the bell — soft translucent-reading pink
const PINK_DEEP = "#d8759d";   // shading (inner bell, tentacles)
const CREAM = "#f6efe3";       // rim frill + eye whites
const EYE_DARK = "#3b3230";    // pupils + smile (warm dark, not black)
const CHEEK = "#e88aa8";       // rosy cheek dots

const RIM_Y = 0.38;            // height of the bell's rim (the pulse pivot)
const BELL_R = 0.21;           // bell radius

// One dangling wavy tentacle: a chain of small spheres stepping down from the
// rim with alternating sideways offsets (the wave), ending in a slightly
// bigger tip blob that rests on the ground. `angle` places it around the rim.
function tentacle(angle: number, name?: string): Part[] {
  const dx = Math.sin(angle), dz = Math.cos(angle);
  const rr = 0.13;             // radial distance of the chain from the axis
  // Perpendicular direction on the ground plane, for the sideways wave.
  const px = Math.cos(angle), pz = -Math.sin(angle);
  const blob = (y: number, wave: number, r: number): Part => ({
    geometry: new THREE.SphereGeometry(r, 8, 6),
    color: PINK_DEEP,
    position: [dx * rr + px * wave, y, dz * rr + pz * wave],
    scale: [1, 1.5, 1],
    name,
  });
  return [
    blob(0.32, 0.0, 0.036),
    blob(0.24, 0.022, 0.033),
    blob(0.16, -0.022, 0.03),
    blob(0.08, 0.018, 0.028),
    // The tip: a touch bigger, sitting ON the ground (its centre one radius up).
    {
      geometry: new THREE.SphereGeometry(0.032, 8, 6),
      color: PINK_DEEP,
      position: [dx * rr - px * 0.01, 0.032, dz * rr - pz * 0.01],
      name,
    },
  ];
}

// The pivot a named tentacle sways about: where it hangs from the rim.
function tentPivot(angle: number): [number, number, number] {
  return [Math.sin(angle) * 0.13, RIM_Y, Math.cos(angle) * 0.13];
}

const TENT_COUNT = 8;
const TSLOT = (i: number) => (i + 0.5) * ((Math.PI * 2) / TENT_COUNT);
const SWAY_ANGLES = [TSLOT(0), TSLOT(2), TSLOT(4), TSLOT(6)];
const STILL_ANGLES = [TSLOT(1), TSLOT(3), TSLOT(5), TSLOT(7)];

export const jellyfish = defineModel({
  id: "jellyfish",
  batchId: "mess",
  name: "Jellyfish",
  tags: ["creature", "mess", "cute", "animated", "sea", "ocean", "jellyfish", "animal"],
  summary: "A soft pink jellyfish, bell pulsing over a skirt of wavy tentacles.",
  description:
    "A storybook jellyfish: a translucent-pink dome bell with a cream frill " +
    "at the rim, big close-set eyes and rosy cheeks on the front, and a " +
    "curtain of wavy tentacles dangling to the floor so it stands on its own " +
    "skirt. The idle hovers and sways the tentacles; bell-pulse squashes and " +
    "pops the bell about its rim like a real jelly's swim stroke.",
  author: "models",
  footprint: 0.4,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── the bell ─────────────────────────────────────────────────────────
      // Main dome: a sphere flattened into a bell, its equator at the rim.
      { geometry: new THREE.SphereGeometry(BELL_R, 14, 12), color: PINK,
        position: [0, RIM_Y + 0.04, 0], scale: [1.0, 0.95, 1.0], name: "bell" },
      // A deeper-pink inner skirt peeking below the dome edge.
      { geometry: new THREE.SphereGeometry(BELL_R * 0.88, 12, 10), color: PINK_DEEP,
        position: [0, RIM_Y + 0.005, 0], scale: [1.0, 0.55, 1.0], name: "bell" },
      // Cream frill: a torus hugging the rim like a skirt hem.
      { geometry: new THREE.TorusGeometry(BELL_R * 0.9, 0.032, 8, 24), color: CREAM,
        position: [0, RIM_Y, 0], rotation: [Math.PI / 2, 0, 0], name: "bell" },
      // A little cream crown bobble on top for charm.
      { geometry: new THREE.SphereGeometry(0.035, 8, 6), color: CREAM,
        position: [0, RIM_Y + 0.24, 0], name: "bell" },

      // ── the face (big and close together, on the bell front) ────────────
      // Eye whites.
      { geometry: new THREE.SphereGeometry(0.05, 12, 10), color: CREAM,
        position: [-0.07, RIM_Y + 0.08, 0.17], name: "bell" },
      { geometry: new THREE.SphereGeometry(0.05, 12, 10), color: CREAM,
        position: [0.07, RIM_Y + 0.08, 0.17], name: "bell" },
      // Dark pupils, set slightly forward.
      { geometry: new THREE.SphereGeometry(0.026, 10, 8), color: EYE_DARK,
        position: [-0.068, RIM_Y + 0.08, 0.209], name: "bell" },
      { geometry: new THREE.SphereGeometry(0.026, 10, 8), color: EYE_DARK,
        position: [0.068, RIM_Y + 0.08, 0.209], name: "bell" },
      // Tiny cream highlights for sparkle.
      { geometry: new THREE.SphereGeometry(0.009, 8, 6), color: CREAM,
        position: [-0.057, RIM_Y + 0.092, 0.229], name: "bell" },
      { geometry: new THREE.SphereGeometry(0.009, 8, 6), color: CREAM,
        position: [0.079, RIM_Y + 0.092, 0.229], name: "bell" },
      // Rosy cheeks.
      { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: CHEEK,
        position: [-0.105, RIM_Y + 0.035, 0.155], scale: [1, 0.7, 0.6], name: "bell" },
      { geometry: new THREE.SphereGeometry(0.024, 8, 6), color: CHEEK,
        position: [0.105, RIM_Y + 0.035, 0.155], scale: [1, 0.7, 0.6], name: "bell" },
      // A small smile: thin dark torus sunk into the bell, front arc showing.
      { geometry: new THREE.TorusGeometry(0.04, 0.008, 8, 20), color: EYE_DARK,
        position: [0, RIM_Y + 0.03, 0.178], rotation: [Math.PI / 2 + 0.25, 0, 0], name: "bell" },

      // ── the tentacles ────────────────────────────────────────────────────
      // Four named swayers at the corners + four straight hangers between.
      ...tentacle(SWAY_ANGLES[0], "tent-0"),
      ...tentacle(SWAY_ANGLES[1], "tent-1"),
      ...tentacle(SWAY_ANGLES[2], "tent-2"),
      ...tentacle(SWAY_ANGLES[3], "tent-3"),
      ...STILL_ANGLES.flatMap((a) => tentacle(a)),
    ];
  },
  animations: [
    // 1. Idle — a gentle hover bob of the bell (the tentacle tips stay near
    //    the floor) while the named tentacles sway in alternating phases.
    {
      id: "idle",
      label: "Idle",
      duration: 3.2,
      loop: true,
      tracks: [
        {
          target: "bell", pivot: [0, RIM_Y, 0],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.6, value: [0, 0.03, 0], ease: "sine" },
            { t: 3.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tent-0", pivot: tentPivot(SWAY_ANGLES[0]),
          rotation: [
            { t: 0, value: [0.08, 0, 0], ease: "sine" },
            { t: 1.6, value: [-0.08, 0, 0], ease: "sine" },
            { t: 3.2, value: [0.08, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tent-1", pivot: tentPivot(SWAY_ANGLES[1]),
          rotation: [
            { t: 0, value: [-0.08, 0, 0], ease: "sine" },
            { t: 1.6, value: [0.08, 0, 0], ease: "sine" },
            { t: 3.2, value: [-0.08, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tent-2", pivot: tentPivot(SWAY_ANGLES[2]),
          rotation: [
            { t: 0, value: [0.08, 0, 0], ease: "sine" },
            { t: 1.6, value: [-0.08, 0, 0], ease: "sine" },
            { t: 3.2, value: [0.08, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tent-3", pivot: tentPivot(SWAY_ANGLES[3]),
          rotation: [
            { t: 0, value: [-0.08, 0, 0], ease: "sine" },
            { t: 1.6, value: [0.08, 0, 0], ease: "sine" },
            { t: 3.2, value: [-0.08, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // 2. Bell-pulse — the signature move: the bell squashes wide, then pops
    //    tall and narrow about its rim (a jelly's swim stroke), riding up on
    //    the pop while the tentacles trail behind the beat.
    {
      id: "bell-pulse",
      label: "Bell pulse",
      duration: 2.0,
      loop: true,
      tracks: [
        {
          target: "bell", pivot: [0, RIM_Y, 0],
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 0.5, value: [1.12, 0.82, 1.12], ease: "easeInOut" },
            { t: 1.0, value: [0.92, 1.18, 0.92], ease: "easeOut" },
            { t: 2.0, value: [1, 1, 1], ease: "sine" },
          ],
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [0, -0.015, 0], ease: "easeInOut" },
            { t: 1.0, value: [0, 0.05, 0], ease: "easeOut" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          // The tentacles flare out on the squash and trail in on the pop.
          target: "tent-0", pivot: tentPivot(SWAY_ANGLES[0]),
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [-0.22, 0, 0], ease: "easeInOut" },
            { t: 1.1, value: [0.15, 0, 0], ease: "easeOut" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tent-1", pivot: tentPivot(SWAY_ANGLES[1]),
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [-0.22, 0, 0], ease: "easeInOut" },
            { t: 1.1, value: [0.15, 0, 0], ease: "easeOut" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tent-2", pivot: tentPivot(SWAY_ANGLES[2]),
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [-0.22, 0, 0], ease: "easeInOut" },
            { t: 1.1, value: [0.15, 0, 0], ease: "easeOut" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "tent-3", pivot: tentPivot(SWAY_ANGLES[3]),
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 0.5, value: [-0.22, 0, 0], ease: "easeInOut" },
            { t: 1.1, value: [0.15, 0, 0], ease: "easeOut" },
            { t: 2.0, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },
  ],
});
